import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, CheckCircle2, XCircle, Info, Clock, Check, X, Mail, Copy, Eye, Truck, RotateCcw, RefreshCw, Loader2, LayoutDashboard, Building2, Users as UsersIcon, Receipt, ShieldAlert, Boxes, SlidersHorizontal, ShieldCheck, TrendingUp, Coins, Plug, FileSignature, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import UserAccessDialog from "@/components/UserAccessDialog";
import ShipStationStoreSelect from "@/components/ShipStationStoreSelect";
import BillingPortal from "@/components/BillingPortal";
import AdminClaims from "@/components/AdminClaims";
import WeeklyEmails from "@/components/WeeklyEmails";
import { AdminOverview, AdminReturns } from "@/components/AdminExtras";
import AdminInventory from "@/components/AdminInventory";
import OpsConsole from "@/components/OpsConsole";
import AdminSearch from "@/components/AdminSearch";

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isReturnsManager, setViewAs, can, isSuperAdmin } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [section, setSection] = useState(() => {
    const s = new URLSearchParams(window.location.search).get("section") || "overview";
    return s === "home" ? "overview" : s;
  });
  // The customer whose setup screens are open — remembered across sections and reloads.
  const [customerId, setCustomerId] = useState(() =>
    new URLSearchParams(window.location.search).get("account") || localStorage.getItem("bh_ops_customer") || ""
  );
  const [customerTab, setCustomerTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const selectedCustomer = accounts.find((a) => a.id === customerId) || null;
  useEffect(() => {
    if (customerId) localStorage.setItem("bh_ops_customer", customerId);
  }, [customerId]);

  // Deep-link support from the cockpit "things to do" board (?section=users).
  useEffect(() => {
    const s = new URLSearchParams(location.search).get("section");
    if (s) setSection(s === "home" ? "overview" : s);
  }, [location.search]);

  // Keep the URL in step so a link comes back to the same screen and customer.
  useEffect(() => {
    const p = new URLSearchParams();
    p.set("section", section);
    if (section === "customer") {
      if (customerId) p.set("account", customerId);
      p.set("tab", customerTab);
    }
    const next = `/admin?${p.toString()}`;
    if (`${location.pathname}${location.search}` !== next) navigate(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, customerId, customerTab]);

  const canBilling = can("billing", "view");

  const loadAll = async () => {
    setLoading(true);
    try {
      if (isReturnsManager) {
        // Returns managers can't see accounts/users/pricing — only the staff store list.
        const s = await api.get("/staff/accounts");
        setAccounts(s.data.accounts || []);
      } else {
        const [a, u, s] = await Promise.all([
          api.get("/admin/accounts"),
          api.get("/admin/users"),
          api.get("/admin/integration-status"),
        ]);
        setAccounts(a.data.accounts);
        setUsers(u.data.users);
        setIntegrationStatus(s.data);
      }
    } catch (e) {
      toast.error("Could not load admin data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  // The Admin Console is BoxHero's own cockpit — never viewed "as" a client.
  useEffect(() => { setViewAs("all", "All accounts"); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="inline-block text-xs uppercase tracking-wider font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
            Admin Console
          </span>
          <h1 className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Manrope" }}>{isReturnsManager ? "Warehouse & Ops Console" : "Operations Console"}</h1>
          <p className="text-slate-500 mt-1">{isReturnsManager ? "Process returns, manage claims and receive inbound inventory." : "Your internal cockpit — accounts, users, billing, claims and more."}</p>
        </div>
        {!isReturnsManager && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <AdminSearch onSection={setSection} />
          <div className="flex items-center gap-2">
          <button
            data-testid="invite-customer-btn"
            onClick={() => setInviting(true)}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2.5 rounded-md transition-colors"
          >
            <Mail className="w-4 h-4" /> Invite customer
          </button>
          <button
            data-testid="new-account-btn"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-orange-400 hover:text-orange-600 text-slate-700 font-semibold px-4 py-2.5 rounded-md transition-colors"
          >
            <Plus className="w-4 h-4" /> New account
          </button>
        </div>
      </div>
        )}
      </div>


      {/* Pending approvals banner */}
      {!isReturnsManager && users.filter((u) => u.status === "pending").length > 0 && (
        <div data-testid="pending-banner" className="flex items-start gap-3 bg-orange-50 border border-orange-300 rounded-lg px-4 py-3">
          <Clock className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <strong className="text-orange-900">
              {users.filter((u) => u.status === "pending").length} pending approval{users.filter((u) => u.status === "pending").length === 1 ? "" : "s"}
            </strong>
            <span className="text-orange-800"> — new users are waiting for you to review and link them to a customer account. </span>
            <a href="#users" onClick={(e) => { e.preventDefault(); setSection("users"); }} className="font-semibold text-orange-900 underline hover:no-underline">
              Review now →
            </a>
          </div>
        </div>
      )}

      {/* Integration status lives in Settings → Integrations, not on every screen */}

      <Tabs value={section} onValueChange={setSection} className="space-y-6">
        <div className="flex flex-col lg:flex-row gap-5 items-start">
          <OpsRail
            section={section}
            onSection={setSection}
            accounts={accounts}
            accountId={customerId}
            onAccount={setCustomerId}
            customerTab={customerTab}
            onCustomerTab={(t) => { setCustomerTab(t); setSection("customer"); }}
            can={can}
            isReturnsManager={isReturnsManager}
            isSuperAdmin={isSuperAdmin}
            counts={{ accounts: accounts.length, users: users.filter((u) => u.status === "pending").length }}
          />

        <div className="min-w-0 flex-1 w-full">
          <TabsContent value="customer" className="mt-0">
            {selectedCustomer ? (
              <OpsConsole
                accounts={accounts}
                users={users}
                fixedAccountId={selectedCustomer.id}
                tab={customerTab}
                onTabChange={setCustomerTab}
                onEditAccount={() => setEditing(selectedCustomer)}
                onEditUser={() => setSection("users")}
              />
            ) : (
              <div data-testid="customer-none" className="bg-white border border-slate-200 rounded-xl p-12 text-center">
                <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-lg font-semibold text-slate-600">Pick a customer</p>
                <p className="text-sm text-slate-500 mt-1">Choose a customer on the left to set up their stores, rate card, contract and users.</p>
              </div>
            )}
          </TabsContent>
          <TabsContent value="customers" className="mt-0">
            <OpsConsole accounts={accounts} users={users} onEditUser={() => setSection("users")} />
          </TabsContent>

          <TabsContent value="overview" className="mt-0">
            <AdminOverview accounts={accounts} users={users} onGoto={setSection} />
          </TabsContent>

          <TabsContent value="accounts" className="mt-0">
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            {loading ? (
              <div className="p-10 text-center text-slate-500">Loading…</div>
            ) : (
              <table className="w-full text-left" data-testid="accounts-table">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="py-3 px-4 font-medium">Company</th>
                    <th className="py-3 px-4 font-medium">BoxHero ID</th>
                    <th className="py-3 px-4 font-medium">Primary Email</th>
                    <th className="py-3 px-4 font-medium">Stripe Customer</th>
                    <th className="py-3 px-4 font-medium">ShipStation Store</th>
                    <th className="py-3 px-4 font-medium">Users</th>
                    <th className="py-3 px-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.id} data-testid={`account-row-${a.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                      <td className="py-3 px-4 text-sm font-semibold text-slate-900">{a.company_name}</td>
                      <td className="py-3 px-4 text-sm font-mono text-blue-700">{a.boxhero_account_id}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{a.primary_email}</td>
                      <td className="py-3 px-4 text-sm">
                        {a.stripe_customer_id ? (
                          <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{a.stripe_customer_id}</code>
                        ) : <span className="text-slate-400 italic text-xs">not linked</span>}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {(a.shipstation_store_ids?.length ? a.shipstation_store_ids : (a.shipstation_store_id ? [a.shipstation_store_id] : [])).length ? (
                          <span className="flex flex-wrap gap-1">
                            {(a.shipstation_store_ids?.length ? a.shipstation_store_ids : [a.shipstation_store_id]).map((sid) => (
                              <code key={sid} className="text-xs bg-slate-100 px-2 py-0.5 rounded">{sid}</code>
                            ))}
                          </span>
                        ) : <span className="text-slate-400 italic text-xs">not linked</span>}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700">{a.users_count}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-3 justify-end">
                          {(a.shipstation_store_id || a.stripe_customer_id) && (
                            <button
                              data-testid={`impersonate-account-${a.id}`}
                              onClick={() => { setViewAs(a.id, a.company_name); navigate("/"); }}
                              className="inline-flex items-center gap-1 text-slate-600 hover:text-orange-600 text-xs font-semibold"
                              title="View this customer's dashboard (read-only)"
                            >
                              <Eye className="w-3.5 h-3.5" /> View as
                            </button>
                          )}
                          <button
                            data-testid={`edit-account-${a.id}`}
                            onClick={() => setEditing(a)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-semibold"
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {accounts.length === 0 && (
                    <tr><td colSpan={7} className="py-10 text-center text-slate-500">No accounts yet.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <UsersTable users={users} accounts={accounts} onChanged={loadAll} />
          </div>
        </TabsContent>

        <TabsContent value="carriers" className="mt-4">
          <CarrierMappings />
        </TabsContent>

        <TabsContent value="billing" className="mt-0">
          {canBilling ? <BillingPortal accounts={accounts} initialTab="run" isSuperAdmin={isSuperAdmin} showRail={false} /> : <BillingLocked />}
        </TabsContent>

        <TabsContent value="rate-cards" className="mt-0">
          {canBilling ? <BillingPortal accounts={accounts} initialTab="rates" isSuperAdmin={isSuperAdmin} showRail={false} /> : <BillingLocked />}
        </TabsContent>

        <TabsContent value="profit" className="mt-0">
          {isSuperAdmin ? <BillingPortal accounts={accounts} initialTab="profit" isSuperAdmin showRail={false} /> : <SuperAdminOnly />}
        </TabsContent>

        <TabsContent value="costs" className="mt-0">
          {isSuperAdmin ? <BillingPortal accounts={accounts} initialTab="costs" isSuperAdmin showRail={false} /> : <SuperAdminOnly />}
        </TabsContent>

        <TabsContent value="integrations" className="mt-0">
          {integrationStatus ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <IntegrationCard
                name="Stripe API"
                configured={integrationStatus.stripe_configured}
                instructions={integrationStatus.instructions.stripe}
                testId="stripe-status-card"
              />
              <IntegrationCard
                name="ShipStation API"
                configured={integrationStatus.shipstation_configured}
                instructions={integrationStatus.instructions.shipstation}
                testId="shipstation-status-card"
              />
            </div>
          ) : <p className="text-slate-500 text-sm">Loading…</p>}
        </TabsContent>

        <TabsContent value="credits" className="mt-0">
          {canBilling ? <BillingPortal accounts={accounts} initialTab="credits" isSuperAdmin={isSuperAdmin} showRail={false} /> : <BillingLocked />}
        </TabsContent>

        <TabsContent value="claims" className="mt-4">
          <AdminClaims />
        </TabsContent>

        <TabsContent value="emails" className="mt-4">
          <WeeklyEmails accounts={accounts} />
        </TabsContent>

        <TabsContent value="returns" className="mt-0">
          <AdminReturns />
        </TabsContent>

        <TabsContent value="inventory" className="mt-0">
          <AdminInventory isReturnsManager={isReturnsManager} />
        </TabsContent>
        </div>
        </div>
      </Tabs>

      {(editing || creating) && (
        <AccountDialog
          account={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={async () => { await loadAll(); setEditing(null); setCreating(false); }}
        />
      )}

      {inviting && (
        <InviteCustomerDialog
          accounts={accounts}
          onClose={() => setInviting(false)}
          onSent={async () => { await loadAll(); setInviting(false); }}
        />
      )}
    </div>
  );
}


const COMPANY_NAV = [
  { g: "Across all customers" },
  { v: "overview", l: "Overview", icon: LayoutDashboard, mgmt: true },
  { v: "billing", l: "Run the month", icon: Receipt, module: "billing", sensitive: true },
  { v: "profit", l: "Profit by client", icon: TrendingUp, superOnly: true, sensitive: true },
  { v: "credits", l: "Credits given", icon: ShieldAlert, module: "billing", sensitive: true },
  { g: "Warehouse queues" },
  { v: "inventory", l: "Receiving & inventory", icon: Boxes, module: "inventory" },
  { v: "returns", l: "Returns", icon: RotateCcw, module: "returns" },
  { v: "claims", l: "Claims", icon: ShieldAlert, module: "claims" },
  { g: "Set up & admin" },
  { v: "accounts", l: "Accounts", icon: Building2, countKey: "accounts", mgmt: true },
  { v: "users", l: "Users & approvals", icon: UsersIcon, countKey: "pending", mgmt: true },
  { v: "costs", l: "Our Costs", icon: Coins, superOnly: true, sensitive: true },
  { v: "carriers", l: "Carriers", icon: Truck, mgmt: true },
  { v: "emails", l: "Weekly emails", icon: Mail, mgmt: true },
  { v: "integrations", l: "Integrations", icon: Plug, mgmt: true },
];

// Everything you configure for one brand. Drives OpsConsole's per-customer panels.
const CUSTOMER_NAV = [
  { v: "overview", l: "Overview", icon: LayoutDashboard },
  { v: "billing", l: "Monthly bill", icon: Receipt, module: "billing", sensitive: true },
  { v: "pricing", l: "Rate card", icon: SlidersHorizontal, module: "billing", sensitive: true },
  { v: "contract", l: "Contract", icon: FileSignature },
  { v: "claims", l: "Claims", icon: ShieldAlert },
  { v: "documents", l: "Documents", icon: FolderOpen },
  { v: "emails", l: "Emails", icon: Mail },
  { v: "users", l: "Users & access", icon: UsersIcon },
];

/** The console's only navigation: company-wide work on top, one customer's setup below. */
function OpsRail({ section, onSection, accounts, accountId, onAccount, customerTab, onCustomerTab, can, isReturnsManager, isSuperAdmin, counts = {} }) {
  const allowed = (i) => {
    if (i.superOnly) return isSuperAdmin;
    if (i.module) return can(i.module, "view");
    return !isReturnsManager;
  };
  const item = (i, active, onClick, testId) => (
    <button
      key={i.v}
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-sm font-semibold transition-colors ${
        active ? "bg-bh-navy-deep text-white" : i.sensitive ? "text-red-700 hover:bg-red-50" : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      <i.icon className="w-4 h-4 shrink-0" />
      <span className="truncate flex-1">{i.l}</span>
      {i.countKey && counts[i.countKey] ? (
        <span className={`text-[11px] font-bold tabular-nums ${active ? "text-white/70" : "text-slate-400"}`}>{counts[i.countKey]}</span>
      ) : null}
      {i.sensitive && !active && <ShieldCheck className="w-3 h-3 text-red-400 shrink-0" title="Sensitive — financials" />}
    </button>
  );

  return (
    <nav data-testid="ops-rail" className="w-full lg:w-72 shrink-0 bg-white border border-slate-200 rounded-xl p-2.5 space-y-4 lg:sticky lg:top-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400 px-2 mb-1.5">Company</p>
        {COMPANY_NAV.map((i, idx) =>
          i.g ? (
            idx === 0 ? null : <p key={i.g} className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-300 px-2 pt-2 pb-1">{i.g}</p>
          ) : allowed(i) ? item(i, section === i.v, () => onSection(i.v), `rail-company-${i.v}`) : null
        )}
      </div>

      {!isReturnsManager && (
        <div className="pt-3 border-t border-slate-100">
          <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400 px-2 mb-1.5">Customer setup</p>
          <select
            data-testid="rail-customer-select"
            value={accountId || ""}
            onChange={(e) => { onAccount(e.target.value); onCustomerTab(customerTab || "overview"); }}
            className="w-full mb-1.5 px-2.5 py-2 border border-slate-300 rounded-lg text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option value="">Choose a customer…</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
          </select>
          {CUSTOMER_NAV.filter(allowed).map((i) =>
            item(i, section === "customer" && customerTab === i.v, () => onCustomerTab(i.v), `rail-customer-${i.v}`)
          )}
          <p className="text-[11px] text-slate-400 px-2 pt-2 leading-relaxed">
            These screens always stay on the customer picked above. Bills, profit and queues live under Company.
          </p>
        </div>
      )}
    </nav>
  );
}

function BillingLocked() {
  return (
    <div data-testid="billing-locked" className="bg-slate-50 border border-slate-200 rounded-xl p-10 text-center">
      <div className="w-12 h-12 rounded-xl bg-slate-200 text-slate-500 flex items-center justify-center mx-auto mb-3">
        <ShieldCheck className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: "Manrope" }}>Billing is restricted</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">A super admin has not granted you access to billing and pricing. Ask Sergey or an admin to enable it from your Access settings.</p>
    </div>
  );
}

function SuperAdminOnly() {
  return (
    <div data-testid="super-admin-only" className="bg-slate-50 border border-slate-200 rounded-xl p-10 text-center">
      <div className="w-12 h-12 rounded-xl bg-slate-200 text-slate-500 flex items-center justify-center mx-auto mb-3">
        <ShieldCheck className="w-6 h-6" />
      </div>
      <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: "Manrope" }}>Super admins only</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Our own margin and cost figures are limited to super admins. Billing and rate cards are unaffected.</p>
    </div>
  );
}

function IntegrationCard({ name, configured, instructions, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${configured ? "bg-green-100 text-green-600" : "bg-slate-100 text-slate-500"}`}>
          {configured ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-slate-900">{name}</h3>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${configured ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
              {configured ? "Configured" : "Not configured"}
            </span>
          </div>
          <p className="text-xs text-slate-600 mt-2 leading-relaxed">{instructions}</p>
        </div>
      </div>
    </div>
  );
}

function AccountDialog({ account, onClose, onSaved }) {
  const isNew = !account;
  const [form, setForm] = useState({
    company_name: account?.company_name || "",
    boxhero_account_id: account?.boxhero_account_id || "",
    primary_email: account?.primary_email || "",
    stripe_customer_id: account?.stripe_customer_id || "",
    shipstation_store_ids: account?.shipstation_store_ids?.length
      ? account.shipstation_store_ids.map(String)
      : (account?.shipstation_store_id ? [String(account.shipstation_store_id)] : []),
    shipstation_customer_ids_text: (account?.shipstation_customer_ids?.length
      ? account.shipstation_customer_ids
      : (account?.shipstation_customer_id ? [account.shipstation_customer_id] : [])).join(", "),
    whatsapp_group_url: account?.whatsapp_group_url || "",
    status: account?.status || "active",
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingWelcome, setSendingWelcome] = useState(false);
  const ob = account?.onboarding;

  const handleSendWelcome = async () => {
    if (!form.primary_email) { toast.error("Add a primary email first, then save."); return; }
    if (!window.confirm(`Send a welcome / onboarding email to ${form.primary_email}?`)) return;
    setSendingWelcome(true);
    try {
      const { data } = await api.post(`/admin/accounts/${account.id}/send-welcome`, {});
      if (data.email_sent) {
        toast.success(`Welcome email sent to ${form.primary_email}`);
      } else {
        toast.warning(`Onboarding enabled, but email failed: ${data.email_error || "unknown error"}. Link: ${data.action_url}`);
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingWelcome(false);
    }
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    const { shipstation_customer_ids_text, ...rest } = form;
    const payload = {
      ...rest,
      shipstation_customer_ids: (shipstation_customer_ids_text || "")
        .split(",").map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (isNew) {
        await api.post("/admin/accounts", payload);
        toast.success("Account created");
      } else {
        await api.patch(`/admin/accounts/${account.id}`, payload);
        toast.success("Account updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${account.company_name}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/accounts/${account.id}`);
      toast.success("Account deleted");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-white" data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: "Manrope" }}>
            {isNew ? "New Customer Account" : `Edit ${account.company_name}`}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <Field label="Company Name" testId="acc-company-name" value={form.company_name} onChange={onChange("company_name")} />
          <Field label="BoxHero Account ID" testId="acc-boxhero-id" value={form.boxhero_account_id} onChange={onChange("boxhero_account_id")} placeholder="BH-1001" />
          <Field label="Primary Email" testId="acc-primary-email" type="email" value={form.primary_email} onChange={onChange("primary_email")} className="md:col-span-2" />
          <Field label="Stripe Customer ID" testId="acc-stripe-cid" value={form.stripe_customer_id} onChange={onChange("stripe_customer_id")} placeholder="cus_..." className="md:col-span-2" />
          <ShipStationStoreSelect
            multiple
            label="ShipStation Stores (select all this client sells through)"
            value={form.shipstation_store_ids}
            onChange={(v) => setForm((f) => ({ ...f, shipstation_store_ids: v }))}
          />
          <Field label="ShipStation Customer IDs (optional, comma separated)" testId="acc-shipstation-customer" value={form.shipstation_customer_ids_text} onChange={onChange("shipstation_customer_ids_text")} className="md:col-span-2" placeholder="Leave blank if linking by store only" />
          <Field label="WhatsApp group link (unique per client)" testId="acc-whatsapp-url" value={form.whatsapp_group_url} onChange={onChange("whatsapp_group_url")} className="md:col-span-2" placeholder="https://chat.whatsapp.com/..." />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2 flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-800 leading-relaxed">
            Stripe Customer IDs start with <code className="bg-white px-1 rounded">cus_</code> (find them at dashboard.stripe.com → Customers).
            The ShipStation store list above is loaded live from your ShipStation account — just search and pick.
          </p>
        </div>

        {!isNew && (
          <div className="border border-slate-200 rounded-lg p-4 mt-3" data-testid="onboarding-admin-section">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-800" style={{ fontFamily: "Manrope" }}>Customer onboarding</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Stage prices &amp; upload the signed contract first, then send the welcome email so they can set up their own payment &amp; store access.
                </p>
              </div>
              <button
                data-testid="send-welcome-btn"
                onClick={handleSendWelcome}
                disabled={sendingWelcome}
                className="inline-flex items-center gap-1.5 whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-3.5 py-2 rounded-md transition-colors"
              >
                <Mail className="w-4 h-4" /> {sendingWelcome ? "Sending…" : ob?.welcome_sent_at ? "Resend welcome" : "Send welcome email"}
              </button>
            </div>
            {ob?.enabled && (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="onboarding-admin-progress">
                <OnboardStep label="Payment" done={ob?.steps?.payment?.done} />
                <OnboardStep label="Contract" done={ob?.steps?.contract?.done} />
                <OnboardStep label="Store access" done={ob?.steps?.store_access?.done} />
                {ob?.completed_at && (
                  <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4 flex items-center justify-between gap-2">
          {!isNew && (
            <button
              data-testid="delete-account-btn"
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-semibold mr-auto"
            >
              <Trash2 className="w-4 h-4" /> {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button
            data-testid="save-account-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-md transition-colors"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, testId, className = "", ...props }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">{label}</label>
      <input
        data-testid={testId}
        {...props}
        className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm text-slate-900"
      />
    </div>
  );
}

function OnboardStep({ label, done }) {
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
      {done ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {label}
    </span>
  );
}

const STATUS_PILL = {
  active: "bg-green-100 text-green-700",
  pending: "bg-orange-100 text-orange-700",
  rejected: "bg-red-100 text-red-700",
};

function StatusPill({ status }) {
  const s = status || "active";
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded capitalize inline-flex items-center gap-1 ${STATUS_PILL[s] || "bg-slate-100 text-slate-700"}`}>
      {s === "pending" && <Clock className="w-3 h-3" />}
      {s === "active" && <Check className="w-3 h-3" />}
      {s === "rejected" && <X className="w-3 h-3" />}
      {s}
    </span>
  );
}

function UsersTable({ users, accounts, onChanged }) {
  const [approving, setApproving] = React.useState(null); // user obj
  const [accessUser, setAccessUser] = React.useState(null); // user obj for permissions/password
  // Sort: pending first, then by created_at desc
  const sorted = React.useMemo(
    () => [...users].sort((a, b) => {
      if (a.status === "pending" && b.status !== "pending") return -1;
      if (b.status === "pending" && a.status !== "pending") return 1;
      return (b.created_at || "").localeCompare(a.created_at || "");
    }),
    [users]
  );

  const handleReject = async (user) => {
    if (!window.confirm(`Reject ${user.email}? They won't be able to access the dashboard.`)) return;
    try {
      await api.post(`/admin/users/${user.id}/reject`);
      toast.success(`${user.email} rejected`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <table className="w-full text-left" data-testid="users-table">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
            <th className="py-3 px-4 font-medium">Name</th>
            <th className="py-3 px-4 font-medium">Email</th>
            <th className="py-3 px-4 font-medium">Role</th>
            <th className="py-3 px-4 font-medium">Company</th>
            <th className="py-3 px-4 font-medium">Status</th>
            <th className="py-3 px-4 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.id}`} className={`border-b border-slate-100 hover:bg-slate-50/60 ${u.status === "pending" ? "bg-orange-50/40" : ""}`}>
              <td className="py-3 px-4 text-sm font-medium text-slate-900">{u.name}</td>
              <td className="py-3 px-4 text-sm text-slate-700">{u.email}</td>
              <td className="py-3 px-4 text-sm">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded capitalize ${
                  u.role === "admin" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"
                }`}>{u.role}</span>
              </td>
              <td className="py-3 px-4 text-sm text-slate-600">{u.account?.company_name || <span className="text-slate-400 italic text-xs">unassigned</span>}</td>
              <td className="py-3 px-4"><StatusPill status={u.status} /></td>
              <td className="py-3 px-4 text-right">
                {u.status === "pending" ? (
                  <div className="inline-flex items-center gap-2 justify-end">
                    <button
                      data-testid={`approve-user-${u.id}`}
                      onClick={() => setApproving(u)}
                      className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
                    >
                      <Check className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button
                      data-testid={`reject-user-${u.id}`}
                      onClick={() => handleReject(u)}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-semibold px-2 py-1.5 rounded-md"
                    >
                      <X className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-3 justify-end">
                    <button
                      data-testid={`access-user-${u.id}`}
                      onClick={() => setAccessUser(u)}
                      className="text-slate-600 hover:text-blue-700 text-xs font-semibold inline-flex items-center gap-1"
                    >
                      <SlidersHorizontal className="w-3.5 h-3.5" /> Access
                    </button>
                    <button
                      data-testid={`edit-user-${u.id}`}
                      onClick={() => setApproving(u)}
                      className="text-blue-600 hover:text-blue-700 text-xs font-semibold inline-flex items-center gap-1"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {approving && (
        <ApproveUserDialog
          user={approving}
          accounts={accounts}
          onClose={() => setApproving(null)}
          onSaved={() => { setApproving(null); onChanged(); }}
        />
      )}
      {accessUser && (
        <UserAccessDialog
          user={accessUser}
          mode="admin"
          onClose={() => setAccessUser(null)}
          onSaved={onChanged}
        />
      )}
    </>
  );
}

function ApproveUserDialog({ user, accounts, onClose, onSaved }) {
  const isPending = user.status === "pending";
  const [accountId, setAccountId] = React.useState(user.account_id || "");
  const [role, setRole] = React.useState(user.role || "owner");
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    setSaving(true);
    const acct = role === "admin" || role === "returns_manager" ? null : (accountId || null);
    try {
      if (isPending) {
        await api.post(`/admin/users/${user.id}/approve`, { account_id: acct, role });
        toast.success(`${user.email} approved`);
      } else {
        await api.patch(`/admin/users/${user.id}`, { account_id: acct, role });
        toast.success(`${user.email} updated`);
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Suggested account if the user's email matches an account primary_email
  const suggestedAccount = accounts.find((a) => a.primary_email === user.email);

  let saveLabel = "Save";
  if (saving) saveLabel = "Saving…";
  else if (isPending) saveLabel = "Approve & link";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white" data-testid="approve-user-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: "Manrope" }}>
            {isPending ? "Approve user" : "Edit user"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1 bg-slate-50 border border-slate-200 rounded-lg p-4 mt-2 text-sm">
          <div className="flex items-center justify-between"><span className="text-slate-500">Name</span><span className="font-medium text-slate-900">{user.name}</span></div>
          <div className="flex items-center justify-between"><span className="text-slate-500">Email</span><span className="font-medium text-slate-900">{user.email}</span></div>
          <div className="flex items-center justify-between"><span className="text-slate-500">Status</span><StatusPill status={user.status} /></div>
        </div>

        <div className="mt-4 space-y-4">
          {role !== "admin" && role !== "returns_manager" && (
          <div>
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">
              Link to customer account
            </label>
            <select
              data-testid="approve-account-select"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm text-slate-900"
            >
              <option value="">— Unassigned —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {`${a.company_name} (${a.boxhero_account_id})  · Stripe ${a.stripe_customer_id ? "✓" : "✗"}  · ShipStation ${a.shipstation_store_id ? "✓" : "✗"}`}
                </option>
              ))}
            </select>
            {suggestedAccount && accountId !== suggestedAccount.id && (
              <button
                type="button"
                onClick={() => setAccountId(suggestedAccount.id)}
                className="mt-2 text-xs text-blue-600 hover:underline"
              >
                💡 Auto-suggest: {suggestedAccount.company_name} (matches user's email as primary)
              </button>
            )}
          </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Role</label>
            <select
              data-testid="approve-role-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm text-slate-900"
            >
              <option value="admin">Admin — BoxHero staff (all stores)</option>
              <option value="returns_manager">Warehouse / Ops — returns, claims & receiving</option>
              <option value="owner">Owner — full access</option>
              <option value="billing">Billing — invoices & subscription only</option>
              <option value="operations">Operations — orders & shipments only</option>
              <option value="viewer">Viewer — read-only</option>
            </select>
            {role === "admin" && (
              <p className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2 leading-relaxed">
                Admins are BoxHero staff with full access to every store and the Admin Console — no customer account link needed.
              </p>
            )}
            {role === "returns_manager" && (
              <p className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 leading-relaxed">
                Warehouse / Ops staff handle returns, claims and inventory receiving — no billing, pricing or account config access.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button
            data-testid="confirm-approve-btn"
            onClick={handleSave}
            disabled={saving}
            className={`disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-md transition-colors ${
              isPending ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {saveLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InviteCustomerDialog({ accounts, onClose, onSent }) {
  const [mode, setMode] = React.useState("existing"); // "existing" | "new"
  const [form, setForm] = React.useState({
    email: "",
    name: "",
    role: "owner",
    account_id: "",
    // new-account fields
    company_name: "",
    boxhero_account_id: "",
    stripe_customer_id: "",
    shipstation_store_id: "",
    shipstation_customer_id: "",
  });
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null); // { invite_url, email_sent, email_error }

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSend = async () => {
    setSending(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        role: form.role,
      };
      const isStaffRole = form.role === "admin" || form.role === "returns_manager";
      if (isStaffRole) {
        // Internal BoxHero staff — no customer account linking needed.
      } else if (mode === "existing") {
        if (!form.account_id) { toast.error("Pick an account to link the customer to."); return; }
        payload.account_id = form.account_id;
      } else {
        if (!form.company_name) { toast.error("Company name is required for a new account."); return; }
        payload.company_name = form.company_name;
        if (form.boxhero_account_id) payload.boxhero_account_id = form.boxhero_account_id;
        if (form.stripe_customer_id) payload.stripe_customer_id = form.stripe_customer_id;
        if (form.shipstation_store_id) payload.shipstation_store_id = form.shipstation_store_id;
        if (form.shipstation_customer_id) payload.shipstation_customer_id = form.shipstation_customer_id;
      }
      const { data } = await api.post("/admin/invites", payload);
      setResult(data);
      if (data.email_sent) {
        toast.success(`Invitation sent to ${form.email}`);
      } else {
        toast.warning("Invite created — but email delivery failed. Copy the link below.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (!result?.invite_url) return;
    navigator.clipboard.writeText(result.invite_url);
    toast.success("Invite link copied");
  };

  if (result) {
    return (
      <Dialog open onOpenChange={(o) => !o && (onSent ? onSent() : onClose())}>
        <DialogContent className="max-w-lg bg-white" data-testid="invite-result-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: "Manrope" }}>
              {result.email_sent ? "Invitation sent ✉️" : "Invitation created"}
            </DialogTitle>
          </DialogHeader>

          <div className={`mt-2 p-4 rounded-lg border ${result.email_sent ? "bg-green-50 border-green-200" : "bg-orange-50 border-orange-200"}`}>
            {result.email_sent ? (
              <p className="text-sm text-green-800">
                We emailed <strong>{form.email}</strong> with a link to set their password and join the dashboard.
              </p>
            ) : (
              <>
                <p className="text-sm text-orange-800 mb-2">
                  Email delivery failed — but the invitation is still valid. Send <strong>{form.email}</strong> the link below manually.
                </p>
                {result.email_error && <p className="text-xs text-orange-700 font-mono bg-orange-100 rounded px-2 py-1">{result.email_error}</p>}
              </>
            )}
          </div>

          <div className="mt-3">
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Invite link (valid 7 days)</label>
            <div className="flex items-center gap-2">
              <input
                readOnly
                data-testid="invite-link-input"
                value={result.invite_url}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-md bg-slate-50 text-sm text-slate-700 font-mono"
              />
              <button
                onClick={copyLink}
                data-testid="copy-invite-link-btn"
                className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-3 py-2 rounded-md transition-colors"
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <button
              data-testid="close-invite-result-btn"
              onClick={() => (onSent ? onSent() : onClose())}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2 rounded-md transition-colors"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-white" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: "Manrope" }}>Invite a customer to BoxHero</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name" testId="invite-name" value={form.name} onChange={onChange("name")} placeholder="Jane Smith" />
            <Field label="Work email" testId="invite-email" type="email" value={form.email} onChange={onChange("email")} placeholder="jane@brand.com" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Role</label>
            <select
              data-testid="invite-role-select"
              value={form.role}
              onChange={onChange("role")}
              className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm text-slate-900"
            >
              <option value="admin">Admin — BoxHero staff (all stores)</option>
              <option value="returns_manager">Warehouse / Ops — returns, claims & receiving (no billing/pricing)</option>
              <option value="owner">Owner — full access</option>
              <option value="billing">Billing — invoices & subscription only</option>
              <option value="operations">Operations — orders & shipments only</option>
              <option value="viewer">Viewer — read-only</option>
            </select>
            {form.role === "admin" && (
              <p className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2 leading-relaxed">
                Admins are BoxHero staff. They see every customer's stores and the Admin Console — no Stripe or ShipStation account linking required.
              </p>
            )}
            {form.role === "returns_manager" && (
              <p className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 leading-relaxed">
                Warehouse / Ops staff handle returns, claims and inventory receiving. They never see billing, pricing, invoices or account config — no account linking required.
              </p>
            )}
          </div>

          {/* Mode toggle — hidden for internal staff */}
          {form.role !== "admin" && form.role !== "returns_manager" && (
          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Customer account</p>
            <div className="flex gap-2 mb-3" data-testid="invite-mode-toggle">
              <button
                type="button"
                onClick={() => setMode("existing")}
                className={`text-sm px-3 py-1.5 rounded-md font-medium transition-colors ${mode === "existing" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                Link to existing account
              </button>
              <button
                type="button"
                onClick={() => setMode("new")}
                className={`text-sm px-3 py-1.5 rounded-md font-medium transition-colors ${mode === "new" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                Create new account
              </button>
            </div>

            {mode === "existing" ? (
              <select
                data-testid="invite-account-select"
                value={form.account_id}
                onChange={onChange("account_id")}
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 text-sm text-slate-900"
              >
                <option value="">— Select an account —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {`${a.company_name} (${a.boxhero_account_id}) · Stripe ${a.stripe_customer_id ? "✓" : "✗"} · ShipStation ${a.shipstation_store_id ? "✓" : "✗"}`}
                  </option>
                ))}
              </select>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Company Name" testId="invite-company-name" value={form.company_name} onChange={onChange("company_name")} placeholder="Tiny Jams" />
                <Field label="BoxHero ID (auto if blank)" testId="invite-bh-id" value={form.boxhero_account_id} onChange={onChange("boxhero_account_id")} placeholder="BH-1002" />
                <Field label="Stripe Customer ID" testId="invite-stripe-cid" className="md:col-span-2" value={form.stripe_customer_id} onChange={onChange("stripe_customer_id")} placeholder="cus_..." />
                <div className="md:col-span-2">
                  <ShipStationStoreSelect
                    value={form.shipstation_store_id}
                    onChange={(v) => setForm((f) => ({ ...f, shipstation_store_id: v }))}
                  />
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4 flex items-start gap-2">
          <Mail className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-800 leading-relaxed">
            We'll email {form.role === "admin" || form.role === "returns_manager" ? "the new team member" : "the customer"} a magic link to set their own password and sign in. The invitation is valid for 7 days. If email delivery fails, you'll get a copyable link as a fallback.
          </p>
        </div>

        <DialogFooter className="mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button
            data-testid="send-invite-btn"
            onClick={handleSend}
            disabled={sending || !form.email || !form.name}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-md transition-colors inline-flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            {sending ? "Sending…" : "Send invitation"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* =====================================================================
   CARRIER NAME MAPPING — raw ShipStation code → admin-defined pretty name
   ===================================================================== */
function CarrierMappings() {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingCode, setSavingCode] = useState(null);
  const [builtAt, setBuiltAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get(`/admin/carriers${refresh ? "?refresh=true" : ""}`);
      setCarriers(data.carriers);
      setBuiltAt(data.usage_built_at || null);
      setDrafts(Object.fromEntries(data.carriers.map((c) => [c.code, c.display_name])));
      if (refresh) toast.success("Carriers and services re-pulled from ShipStation");
    } catch (e) {
      toast.error("Could not load carriers");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (code) => {
    const display_name = (drafts[code] || "").trim();
    if (!display_name) { toast.error("Display name can't be empty"); return; }
    setSavingCode(code);
    try {
      const c = carriers.find((x) => x.code === code);
      await api.put(`/admin/carriers/${code}`, { display_name, api_name: c?.api_name });
      toast.success(`Saved "${display_name}"`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCode(null);
    }
  };

  const reset = async (code) => {
    setSavingCode(code);
    try {
      await api.delete(`/admin/carriers/${code}`);
      toast.success("Reverted to default name");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCode(null);
    }
  };

  return (
    <div data-testid="carrier-mappings" className="space-y-4">
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-900 leading-relaxed">
          These are the carriers pulled from ShipStation. The <strong>API name</strong> is what the provider sends us;
          the <strong>display name</strong> is what customers see on their dashboards. <strong>Services used</strong> is
          the shipping methods we've actually seen on shipments for each carrier. Edit any display name to keep
          things on-brand. Changes apply to every customer.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500" data-testid="carriers-updated-at">
          {builtAt ? `Usage as of ${new Date(builtAt).toLocaleString()} — served from our own copy, so this page loads instantly.` : "Usage is served from our own copy of ShipStation."}
        </p>
        <button
          data-testid="carriers-refresh-btn"
          onClick={() => load(true)}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-slate-400 disabled:opacity-50 text-slate-700 text-xs font-semibold px-3 py-2 rounded-md transition-colors"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {refreshing ? "Refreshing…" : "Refresh from ShipStation"}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading carriers…</div>
        ) : (
          <table className="w-full text-left" data-testid="carriers-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-3 px-4 font-medium">Badge</th>
                <th className="py-3 px-4 font-medium">API name (raw)</th>
                <th className="py-3 px-4 font-medium">Code</th>
                <th className="py-3 px-4 font-medium">Display name (pretty)</th>
                <th className="py-3 px-4 font-medium">Services used</th>
                <th className="py-3 px-4 font-medium text-right">Shipments</th>
                <th className="py-3 px-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {carriers.map((c) => {
                const dirty = (drafts[c.code] || "") !== c.display_name;
                return (
                  <tr key={c.code} data-testid={`carrier-map-row-${c.code}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-3 px-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-[10px] tracking-wide"
                        style={{ backgroundColor: c.bg || "#7A8E9B", color: c.fg || "#FFFFFF", border: c.border ? `2px solid ${c.border}` : undefined }}>
                        {c.short || c.code.slice(0, 4).toUpperCase()}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{c.api_name}</td>
                    <td className="py-3 px-4"><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{c.code}</code></td>
                    <td className="py-3 px-4">
                      <input
                        data-testid={`carrier-input-${c.code}`}
                        value={drafts[c.code] ?? ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [c.code]: e.target.value }))}
                        className="w-full max-w-[220px] px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                      />
                      {c.is_custom && <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-orange-600">custom</span>}
                    </td>
                    <td className="py-3 px-4" data-testid={`carrier-services-${c.code}`}>
                      {(c.services || []).length === 0 ? (
                        <span className="text-xs text-slate-400">No shipments on file</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-[340px]">
                          {c.services.map((s) => (
                            <span key={s.code || s.label} title={`${s.code || "no service code"} · ${s.count.toLocaleString()} shipments`}
                              className="inline-flex items-center gap-1 text-[11px] bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">
                              {s.label}<span className="text-slate-400 tabular-nums">{s.count.toLocaleString()}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right text-sm tabular-nums" data-testid={`carrier-usage-${c.code}`}>
                      {c.shipments_recent ? (
                        <>
                          <span className="font-semibold text-slate-700">{c.shipments_recent.toLocaleString()}</span>
                          {c.last_used && <div className="text-[11px] text-slate-400">last {c.last_used}</div>}
                        </>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <button
                        data-testid={`carrier-save-${c.code}`}
                        onClick={() => save(c.code)}
                        disabled={!dirty || savingCode === c.code}
                        className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
                      >
                        <Check className="w-3.5 h-3.5" /> Save
                      </button>
                      {c.is_custom && (
                        <button
                          data-testid={`carrier-reset-${c.code}`}
                          onClick={() => reset(c.code)}
                          disabled={savingCode === c.code}
                          className="ml-2 inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 text-xs font-semibold"
                          title="Revert to default name"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Reset
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {carriers.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-slate-500">No carriers found. Make sure ShipStation is connected.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
